import { Download, FileText } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { useWorkbookRankings } from '@/services/admin/workbook-analytics.service'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
}

/**
 * Interações com workbooks (downloads/views) por episódio. Top 10
 * no período. Reusa `useWorkbookRankings` que já existia e era usado
 * no dashboard legado.
 *
 * Mapeia o filtro de período do dashboard pro enum 7d/30d/90d/all do hook.
 */
export function WorkbookInteractionsCard({ filters }: Props) {
  // Detecta o "preset" do filtro de data — só pra alimentar o hook
  // que aceita enum (em vez de range arbitrário)
  const period = resolveWorkbookPeriod(filters)
  const { data, isLoading } = useWorkbookRankings('episodes', period, 10, filters.companyIds)

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  const items = data ?? []
  const maxValue = items.length > 0 ? Math.max(...items.map((e) => e.downloads + e.views)) : 1

  return (
    <Card className="glass-card border-border/50 h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="w-4 h-4 text-primary" />
          Workbooks mais acessados
        </CardTitle>
        <CardDescription>
          Top {items.length} episódios · {periodLabel(period)} · views + downloads
        </CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nenhum workbook acessado no período.
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => {
              const total = item.downloads + item.views
              const pct = (total / maxValue) * 100
              return (
                <li key={item.episodeId} className="space-y-1.5">
                  <div className="flex items-baseline justify-between gap-3 text-xs">
                    <span className="font-medium truncate flex-1">{item.episodeTitle}</span>
                    <span className="flex items-center gap-2 text-muted-foreground tabular-nums whitespace-nowrap">
                      <span className="text-sky-400">{item.views}</span>
                      <span className="text-emerald-400 inline-flex items-center gap-0.5">
                        <Download className="w-3 h-3" />
                        {item.downloads}
                      </span>
                    </span>
                  </div>
                  <Progress value={pct} className="h-1.5" />
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function resolveWorkbookPeriod(filters: DashboardFilters): '7d' | '30d' | '90d' | 'all' {
  if (!filters.dateFrom) return 'all'
  const days = Math.round((Date.now() - filters.dateFrom.getTime()) / (1000 * 60 * 60 * 24))
  if (days <= 8) return '7d'
  if (days <= 31) return '30d'
  if (days <= 91) return '90d'
  return 'all'
}

function periodLabel(p: '7d' | '30d' | '90d' | 'all'): string {
  return p === '7d'
    ? 'últimos 7d'
    : p === '30d'
      ? 'últimos 30d'
      : p === '90d'
        ? 'últimos 90d'
        : 'período todo'
}
